import { useState } from 'react'
import { Button, CloseButton, Dialog, Field, HStack, Text, Textarea } from '@chakra-ui/react'
import { ResponsiveModal } from '../../shared'
import { extractApiMessage } from '../../../lib/apiError'
import type { AskResponse, RecordOutcomeRequest, ScheduleMeetingDto } from '../../../types/lobbyDay'

const OPTIONS: Exclude<AskResponse, 'NotRecorded'>[] = ['Yes', 'No', 'Maybe']

interface RecordReportSheetProps {
  meeting: ScheduleMeetingDto | null
  isOpen: boolean
  onClose: () => void
  /** Wired to `editor.recordOutcome` by the board (DB-16). */
  onSave: (req: RecordOutcomeRequest, opts: { onSuccess: () => void; onError: (err: unknown) => void }) => void
}

/** Report-back via the existing outcome PUT. That PUT overwrites notes, so the textarea is
 *  prefilled from `responseNotes` (DB-16). Mount with `key={meeting.id}` so each meeting starts fresh. */
export function RecordReportSheet({ meeting, isOpen, onClose, onSave }: RecordReportSheetProps) {
  const [response, setResponse] = useState<AskResponse>(meeting?.askResponse ?? 'NotRecorded')
  const [notes, setNotes] = useState(meeting?.responseNotes ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<unknown>(null)

  if (!meeting) return null

  const save = () => {
    setSaving(true)
    setError(null)
    onSave(
      { askResponse: response, responseNotes: notes.trim() === '' ? null : notes },
      {
        onSuccess: () => { setSaving(false); onClose() },
        onError: (err) => { setSaving(false); setError(err) },
      })
  }

  return (
    <ResponsiveModal isOpen={isOpen} onClose={onClose} isCentered size="md">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          borderTopRadius={{ base: 'xl', md: 'md' }}
          maxH="85vh"
          pb="env(safe-area-inset-bottom, 0px)">
          <Dialog.CloseTrigger asChild><CloseButton size="sm" /></Dialog.CloseTrigger>
          <Dialog.Header borderBottomWidth="1px">
            <Dialog.Title>Report-back</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body>
            <HStack gap={2} mb={4} role="group" aria-label="Response">
              {OPTIONS.map((o) => (
                <Button
                  key={o}
                  flex={1}
                  minH="44px"
                  variant={response === o ? 'solid' : 'outline'}
                  colorPalette={response === o ? 'brand' : 'gray'}
                  aria-pressed={response === o}
                  onClick={() => setResponse(o)}
                >
                  {o}
                </Button>
              ))}
            </HStack>
            <Field.Root>
              <Field.Label htmlFor="record-report-notes">What they said</Field.Label>
              <Textarea
                id="record-report-notes"
                rows={4}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field.Root>
            {!!error && (
              <Text fontSize="sm" color="danger.fg" mt={3}>{extractApiMessage(error, 'Could not save the report')}</Text>
            )}
          </Dialog.Body>
          <Dialog.Footer borderTopWidth="1px">
            <Button
              colorPalette="brand"
              minH="44px"
              w={{ base: 'full', md: 'auto' }}
              loading={saving}
              disabled={response === 'NotRecorded'}
              onClick={save}
            >
              Save report
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </ResponsiveModal>
  )
}
